import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/manager',
      name: 'manager-dashboard',
      component: () => import('@/views/ManagerDashboardView.vue'),
      meta: { requiresAuth: true, requiresManager: true },
    },
    {
      path: '/timesheets',
      name: 'timesheets',
      component: () => import('@/views/TimesheetsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/timesheets/:id',
      name: 'timesheet-detail',
      component: () => import('@/views/TimesheetDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('@/views/ProjectsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects/hours',
      name: 'project-hours',
      component: () => import('@/views/ProjectHoursView.vue'),
      meta: { requiresAuth: true, requiresProjectLead: true },
    },
    {
      path: '/approvals',
      name: 'approvals',
      component: () => import('@/views/ApprovalsView.vue'),
      meta: { requiresAuth: true, requiresProjectLead: true },
    },
    {
      path: '/reports/projects',
      name: 'reports-projects',
      component: () => import('@/views/ReportsProjectsView.vue'),
      meta: { requiresAuth: true, requiresManager: true },
    },
    {
      path: '/reports/monthly',
      name: 'reports-monthly',
      component: () => import('@/views/ReportsMonthlyView.vue'),
      meta: { requiresAuth: true, requiresManager: true },
    },
    {
      path: '/reports/payroll',
      name: 'reports-payroll',
      component: () => import('@/views/ReportsPayrollView.vue'),
      meta: { requiresAuth: true, requiresManager: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/employees',
      name: 'admin-employees',
      component: () => import('@/views/admin/EmployeesView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/balances',
      name: 'admin-balances',
      component: () => import('@/views/admin/BalanceOverviewView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/projects',
      name: 'admin-projects',
      component: () => import('@/views/admin/ProjectsAdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/holidays',
      name: 'admin-holidays',
      component: () => import('@/views/admin/HolidaysView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/audit-log',
      name: 'admin-audit-log',
      component: () => import('@/views/admin/AuditLogView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/settings',
      name: 'admin-settings',
      component: () => import('@/views/admin/SettingsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    // Mobile views
    {
      path: '/m',
      redirect: '/m/time',
    },
    {
      path: '/m/time',
      name: 'mobile-time',
      component: () => import('@/views/mobile/MobileTimeEntryView.vue'),
      meta: { requiresAuth: true, mobileLayout: true },
    },
    {
      path: '/m/approvals',
      name: 'mobile-approvals',
      component: () => import('@/views/mobile/MobileApprovalsView.vue'),
      meta: { requiresAuth: true, requiresProjectLead: true, mobileLayout: true },
    },
    {
      path: '/m/reports',
      name: 'mobile-reports',
      component: () => import('@/views/mobile/MobileReportsView.vue'),
      meta: { requiresAuth: true, requiresManager: true, mobileLayout: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
    },
  ],
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Initialize auth state from storage
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const requiresProjectLead = to.meta.requiresProjectLead === true
  const requiresManager = to.meta.requiresManager === true
  const requiresAdmin = to.meta.requiresAdmin === true

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  if (requiresProjectLead && !authStore.isProjectLead && !authStore.isManager) {
    next({ name: 'dashboard' })
    return
  }

  if (requiresManager && !authStore.isManager) {
    next({ name: 'dashboard' })
    return
  }

  if (requiresAdmin && !authStore.isAdmin) {
    next({ name: 'dashboard' })
    return
  }

  // Redirect to dashboard if already logged in and trying to access login
  if (to.name === 'login' && authStore.isAuthenticated) {
    if (window.innerWidth <= 768) {
      next({ name: 'mobile-time' })
    } else {
      next({ name: 'dashboard' })
    }
    return
  }

  // Redirect mobile devices from desktop dashboard to mobile view
  if (to.name === 'dashboard' && window.innerWidth <= 768) {
    next({ name: 'mobile-time' })
    return
  }

  next()
})

export default router
